import {HttpClient, HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {inject, Injectable, Injector} from "@angular/core";
import {Router} from "@angular/router";
import {
  LANDING_ROUTE,
  USER_PROFILE_EDIT_ROUTE,
  USER_PROFILE_VIEW_ROUTE
} from "src/app/app-routing.module";
import {BehaviorSubject, tap} from "rxjs";
import {LoaderService} from "./loader.service";
import {RaceService} from "./race.service";
import {FacebookLoginProvider, SocialAuthService, SocialUser} from "@abacritt/angularx-social-login";
import {JWT_RESPONSE, LocalStorageService} from "./local-storage.service";
import {ActionService} from "./action.service";
import {UserDetailService} from "./user-detail.service";
import {PermissionService} from "./permission.service";
import {
  AccountRecoveryUser,
  ActionResult, ADMINISTRATOR_ROLE, AuthServerType, ExternalLoginRequest,
  JwtResponse,
  LoginRequest, ORGANIZER_ROLE,
  RegisterUser,
  TokenRefreshRequest,
  TokenRefreshResponse, User, UserDetail
} from "@model";
import {EnvironmentService} from "./environment.service";
import * as CryptoJS from 'crypto-js';
import {environment} from "@environment";
import {PublicEventsService} from "./public-events.service";
import {CompetitorTableCacheService} from "./competitor-table-cache.service";


export const LOGIN_URL: string = "/api/auth/login";
export const EXTERNAL_LOGIN_URL: string = "/api/auth/externallogin";
export const REFRESH_JWT_TOKEN_URL: string = "/api/auth/refreshtoken";

@Injectable({ providedIn:'root' })
export class AuthenticationService {

  injector: Injector = inject(Injector);
  static readonly NEW_USER_VERIFICATION_FAILED: number = 11;
  static readonly USER_ALREADY_REGISTERED: number = 12;
  static readonly TOKEN_NOT_VALID: number = 13;
  static readonly PASSWORDS_DO_NOT_MATCH: number = 14;
  static readonly TERMS_AND_CONDITIONS_ACCEPTANCE_REQUIRED: number = 16;

  isRefreshing = false;

  private static _jwtResponse: BehaviorSubject<JwtResponse | null> = new BehaviorSubject<JwtResponse | null>(null);
  private static _userRole: BehaviorSubject<String | null> = new BehaviorSubject<String | null>(null);

  get jwtResponse(): BehaviorSubject<JwtResponse | null> {
    return AuthenticationService._jwtResponse;
  }

  get userRole(): BehaviorSubject<String | null> {
    return AuthenticationService._userRole;
  }

  constructor(private http: HttpClient, private router: Router, private loaderService: LoaderService,
              private socialAuthService: SocialAuthService, private actionService: ActionService, private publicEventService: PublicEventsService,
              private localStorageService: LocalStorageService, private permissionSerivce: PermissionService,
              private environmentService:EnvironmentService) {
    this.socialLoginAuthState();
    this.subscribeToJwtChange();
  }
  isAuthenticated(): boolean {
    return !!this.jwtResponse.getValue()?.token;
  }

  isAdminstrator(): boolean {
    return this.userRole.value === ADMINISTRATOR_ROLE;
  }

  isOrganiser(): boolean {
    return this.userRole.value === ORGANIZER_ROLE;
  }

  subscribeToJwtChange() {
    this.jwtResponse.subscribe(val => {
      if (val) {
        if (val.roles) {
          val.roles.forEach((role: string) => {
            this.userRole.next(role);
            this.permissionSerivce.initPermissions(role)
          });
        } else {
          this.userRole.next(null);
          this.permissionSerivce.emptyPermissions();
        }
        this.publicEventService.initUserFavoritesEvent();
      } else {
        this.userRole.next(null);
        this.permissionSerivce.emptyPermissions();
      }
    });
  }

  private saveLoginData(loginRequest: LoginRequest) {
    if (loginRequest.username && loginRequest.password) {
      const encryptedUsername = CryptoJS.AES.encrypt(loginRequest.username, environment.encodingKey).toString();
      const encryptedPassword = CryptoJS.AES.encrypt(loginRequest.password, environment.encodingKey).toString();
      this.localStorageService.saveData('username', encryptedUsername);
      this.localStorageService.saveData('password', encryptedPassword);
      const expirationTime = new Date().getTime() + 7 * 24 * 60 * 60 * 1000;
      this.localStorageService.saveData('expiration', expirationTime.toString());
    }
  }

  private removeLoginData() {
    this.localStorageService.removeData('username');
    this.localStorageService.removeData('password');
    this.localStorageService.removeData('expiration');
  }

  getLoginData(): [string, string] | undefined {
    const expirationTime = this.localStorageService.getData('expiration');
    if (expirationTime && new Date().getTime() < parseInt(expirationTime)) {
      const encryptedUsername = this.localStorageService.getData('username');
      const encryptedPassword = this.localStorageService.getData('password');
      if (encryptedUsername && encryptedPassword) {
        const username = CryptoJS.AES.decrypt(encryptedUsername, environment.encodingKey).toString(CryptoJS.enc.Utf8);
        const password = CryptoJS.AES.decrypt(encryptedPassword, environment.encodingKey).toString(CryptoJS.enc.Utf8);
        return [username, password];
      }
      return undefined
    } else {
      this.removeLoginData();
      return undefined;
    }
  }

  login(loginRequest: LoginRequest, onError: () => void, saveLoginData?: boolean | undefined | null) {
    this.loaderService.startLoading();
    this.http.post<JwtResponse>(this.environmentService.getApiUrl() + LOGIN_URL, loginRequest)
      .subscribe((jwtResponse: JwtResponse) => {
        if (jwtResponse.token) {
          this.jwtResponse.next(jwtResponse);
          this.localStorageService.saveData(JWT_RESPONSE, JSON.stringify(jwtResponse));
          this.injector.get(UserDetailService).fetchUserProfile((userDetails: UserDetail | undefined)=>{
            this.loaderService.dismissLoading();
            if (!userDetails?.id) {
              this.actionService.clearActions();
              this.router.navigate([USER_PROFILE_EDIT_ROUTE]);
            } else {
              if (this.actionService.getActions().length > 0) {
                this.actionService.dispatchActions();
              } else {
                this.router.navigate([USER_PROFILE_VIEW_ROUTE]);
              }
            }
          });
          if (saveLoginData) {
            this.saveLoginData(loginRequest);
          }
          else this.removeLoginData();
        } else {
          this.loaderService.dismissLoading();
          this.localStorageService.removeData(JWT_RESPONSE);
          this.permissionSerivce.emptyPermissions();
          this.jwtResponse.next(null);
        }
      }, (error: any) => {
        this.loaderService.dismissLoading();
        this.localStorageService.removeData(JWT_RESPONSE);
        this.permissionSerivce.emptyPermissions();
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
          onError();
        }
      });
  }

  /// This is used in authentication.interceptor.ts to refresh the JWT token on this line delay:
  // shouldRetry.bind(authenticationService),
  refreshJwtToken(jwtResponse: JwtResponse, tokenRefreshRequest: TokenRefreshRequest) {
    return this.http.post<TokenRefreshResponse>(this.environmentService.getApiUrl() + REFRESH_JWT_TOKEN_URL, tokenRefreshRequest)
      .pipe((
        tap((result: TokenRefreshResponse) => {
          this.isRefreshing = false;
          jwtResponse.refreshToken = result.refreshToken;
          jwtResponse.token = result.accessToken;
          this.jwtResponse.next(jwtResponse);
          this.localStorageService.saveData(JWT_RESPONSE, JSON.stringify(jwtResponse));
        }, (_error: any) => {
          this.isRefreshing = false;
          this.jwtResponse.next(null);
          this.localStorageService.removeData(JWT_RESPONSE);
          this.permissionSerivce.emptyPermissions();
        })));
  }

  logout(withRedirect: boolean = true) {
    this.loaderService.dismissLoading();
    this.injector.get(RaceService).emptyRaces();
    this.injector.get(UserDetailService).emptyCurrentUserDetails();
    this.injector.get(PublicEventsService).emptyPublicEventsServiceStreams();
    this.injector.get(CompetitorTableCacheService).clearAllCompetitorTableCache();
    this.jwtResponse.next(null);
    this.localStorageService.removeData(JWT_RESPONSE);
    if (withRedirect) {
      this.router.navigate([LANDING_ROUTE]);
    }
    this.socialAuthService.signOut();
  }

  signUp(registerUser: RegisterUser, onSuccess: () => void, onError: (errorCode: number) => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<UserDetail>>(this.environmentService.getApiUrl() + "/api/auth/register", registerUser)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((actionResult: ActionResult<UserDetail>) => {
        if (actionResult.resultCode === AuthenticationService.USER_ALREADY_REGISTERED
          || actionResult.resultCode === AuthenticationService.TERMS_AND_CONDITIONS_ACCEPTANCE_REQUIRED) {
          onError(actionResult.resultCode);
        } else {
          onSuccess();
        }
      });
  }

  verification(accountRecoveryUser: AccountRecoveryUser, onSuccess: () => void, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/auth/verification", accountRecoveryUser)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode === AuthenticationService.USER_ALREADY_REGISTERED) {
          onError();
        } else {
          onSuccess();
        }
      });
  }

  accountRecovery(accountRecoveryUser: AccountRecoveryUser, onSuccess: () => void, onError: (value: number) => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/auth/accountrecovery", accountRecoveryUser)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode === AuthenticationService.TOKEN_NOT_VALID) {
          onError(AuthenticationService.TOKEN_NOT_VALID);
        } else if (actionResult.resultCode === AuthenticationService.PASSWORDS_DO_NOT_MATCH) {
          onError(AuthenticationService.PASSWORDS_DO_NOT_MATCH);
        } else {
          onSuccess();
        }
      });
  }

  recoverAccount(user: User, onSuccess: () => void, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/auth/accountrecoveryrequest", user)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          onSuccess();
        } else {
          onError();
        }
      });
  }

  socialLoginAuthState() {
    this.socialAuthService.authState.subscribe((socialUser: SocialUser) => {
      if (socialUser) {
        this.externalLogin(new ExternalLoginRequest(socialUser.provider === 'GOOGLE' ?
          AuthServerType.GOOGLE : AuthServerType.FACEBOOK, socialUser.provider === 'GOOGLE' ? socialUser.idToken : socialUser.authToken))
      }
    });
  }

  facebookSignIn() {
    this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID).then((socialUser: SocialUser) => {
      if (socialUser) {
        this.externalLogin(new ExternalLoginRequest(AuthServerType.FACEBOOK, socialUser.authToken))
      }
    });
  }

  externalLogin(externalLoginRequest: ExternalLoginRequest) {
    this.loaderService.startLoading();
    this.http.post<JwtResponse>(this.environmentService.getApiUrl() + EXTERNAL_LOGIN_URL, externalLoginRequest)
      .subscribe((jwtResponse: JwtResponse) => {
        if (jwtResponse.token) {
          this.jwtResponse.next(jwtResponse);
          this.localStorageService.saveData(JWT_RESPONSE, JSON.stringify(jwtResponse));
          this.injector.get(UserDetailService).fetchUserProfile((userDetails: UserDetail | undefined)=>{
            this.loaderService.dismissLoading();
            if (!userDetails?.id) {
              this.actionService.clearActions();
              this.router.navigate([USER_PROFILE_EDIT_ROUTE]);
            } else {
              if (this.actionService.getActions().length > 0) {
                this.actionService.dispatchActions();
              } else {
                this.router.navigate([USER_PROFILE_VIEW_ROUTE]);
              }
            }
          });
        } else {
          this.loaderService.dismissLoading();
          this.localStorageService.removeData(JWT_RESPONSE);
          this.jwtResponse.next(null);
        }
      }, (error: any) => {
        this.loaderService.dismissLoading();
        this.localStorageService.removeData(JWT_RESPONSE);
        this.permissionSerivce.emptyPermissions();
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {

        }
      });
  }
}
